import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { ErpContext } from '../../context/ErpContext.js';
import { formatNumberForDisplay, getLocaleConfig } from '../../utils/numeros.js';
import { useParams } from 'react-router-dom';
import { useTenantNavigation } from '../../hooks/useTenantNavigation';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  IconButton,
  AppBar,
  Toolbar,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Divider,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Logout as LogoutIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  AccessTime as AccessTimeIcon,
  Payment as PaymentIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import imgLutenteNav from '../../assets/images/LutenteNavTransparent.png';
import { generarPDFCuponera } from '../../utils/cuponeraUtils';

const ClienteCuotasDetalle = () => {
  const { idItem, idUbicacion } = useParams();
  const navigate = useTenantNavigation();
  const { 
    getMisCuotasCliente, 
    verificarSesionCliente,
    crearPreferenciaPagoCliente,
    getParametrosGlobalesCliente,
    formatoNumericoGlobal,
  } = useContext(ErpContext);

  // Configuración de locale para formateo numérico
  const localeConfig = useMemo(
    () => getLocaleConfig(formatoNumericoGlobal || null),
    [formatoNumericoGlobal]
  );
  
  const [cuotas, setCuotas] = useState([]);
  const [paqueteInfo, setPaqueteInfo] = useState(null);
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clienteData, setClienteData] = useState(null);
  const [primeraCuotaImpagaId, setPrimeraCuotaImpagaId] = useState(null);
  const [pagando, setPagando] = useState(false);
  const [polling, setPolling] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [usaCuponera, setUsaCuponera] = useState(true);
  const [showFechaSalidaPdf, setShowFechaSalidaPdf] = useState(true);
  const [generandoCuponera, setGenerandoCuponera] = useState(false);
  const [logoTenant, setLogoTenant] = useState(null);
  const pollingIntervalRef = useRef(null);
  const cuotaPagandoRef = useRef(null);

  useEffect(() => {
    cargarDatos();
    cargarParametrosGlobales();
  }, [idItem, idUbicacion]);

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleFocus = async () => {
      if (polling && cuotaPagandoRef.current) {
        console.log('[Mis Pagos] Ventana con foco nuevamente, verificando pago...');
        await verificarPagoCuota(cuotaPagandoRef.current);
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [polling]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      setError('');

      // Verificar sesión
      const sesionValida = await verificarSesionCliente();
      if (!sesionValida) {
        handleLogout();
        return;
      }

      // Obtener datos del cliente
      const clienteDataStr = localStorage.getItem('clienteData');
      if (clienteDataStr) {
        setClienteData(JSON.parse(clienteDataStr));
      }

      // Cargar cuotas
      const response = await getMisCuotasCliente(idItem, idUbicacion);
      const cuotasOrdenadas = (response.cuotas || []).sort((a, b) => a.cuotaNro - b.cuotaNro);
      setCuotas(cuotasOrdenadas);
      setPaqueteInfo({
        item: response.item,
        ubicacion: response.ubicacion,
        fechaInicio: response.fechaInicio || null,
        paqueteCancelado: response.paqueteCancelado || false
      });
      setEstadisticas(response.estadisticas || null);
      
      // Determinar la primera cuota impaga
      const primerImpaga = cuotasOrdenadas.find(c => !c.pagada);
      if (primerImpaga) {
        setPrimeraCuotaImpagaId(primerImpaga.cuotaNro);
      } else {
        setPrimeraCuotaImpagaId(null);
      }
      
    } catch (err) {
      console.error('Error cargando cuotas:', err);
      
      if (err.response?.status === 401 || err.response?.data?.code === 'TOKEN_EXPIRED') {
        setError('Tu sesión ha expirado. Por favor, ingresá nuevamente.');
        setTimeout(() => handleLogout(), 2000);
      } else {
        setError('Error al cargar las cuotas. Por favor, intentá nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const cargarParametrosGlobales = async () => {
    try {
      const parametros = await getParametrosGlobalesCliente([
        'usaCuponera',
        'logoNavbarTenant',
        'verFechaSalidaYFechaFinPdf',
        'verFechaSalidaPdf',
      ]);
      const fechaViajePdfConfig =
        parametros.verFechaSalidaYFechaFinPdf ?? parametros.verFechaSalidaPdf;
      // Soporta tanto '1'/'0' como 'true'/'false' o booleanos
      setUsaCuponera(
        parametros.usaCuponera === '1' || 
        parametros.usaCuponera === 1 || 
        parametros.usaCuponera === 'true' || 
        parametros.usaCuponera === true
      );
      setLogoTenant(parametros.logoNavbarTenant);
      setShowFechaSalidaPdf(
        fechaViajePdfConfig === 1 ||
        fechaViajePdfConfig === '1' ||
        fechaViajePdfConfig === true ||
        fechaViajePdfConfig === 'true'
      );
    } catch (error) {
      console.error('Error cargando parámetros globales:', error);
      // Por defecto true si hay error
      setUsaCuponera(true);
      setShowFechaSalidaPdf(true);
    }
  };

  const handleDescargarCuponera = async () => {
    try {
      setGenerandoCuponera(true);
      
      setSnackbar({
        open: true,
        message: 'Generando cuponera...',
        severity: 'info'
      });

      // Generar el PDF de la cuponera
      await generarPDFCuponera({
        paqueteInfo,
        cuotas,
        clienteData,
        logoBase64: logoTenant,
        showFechaSalida: showFechaSalidaPdf
      });

      setSnackbar({
        open: true,
        message: 'Cuponera descargada exitosamente',
        severity: 'success'
      });

    } catch (error) {
      console.error('Error generando cuponera:', error);
      setSnackbar({
        open: true,
        message: 'Error al generar la cuponera. Por favor, intenta nuevamente.',
        severity: 'error'
      });
    } finally {
      setGenerandoCuponera(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('clienteToken');
    localStorage.removeItem('clienteData');
    navigate('mispagos/login', { replace: true });
  };

  const handleVolver = () => {
    navigate('mispagos/dashboard');
  };

  const formatearMoneda = (monto) => {
    const formatted = formatNumberForDisplay(monto, { locale: localeConfig.locale });
    return `$${formatted}`;
  };

  const parseFechaLocal = (fecha) => {
    if (!fecha) return null;
    if (fecha instanceof Date) return fecha;
    if (typeof fecha === 'string') {
      const match = fecha.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        const [, year, month, day] = match;
        return new Date(Number(year), Number(month) - 1, Number(day));
      }
    }
    const parsed = new Date(fecha);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatearFecha = (fecha) => {
    const date = parseFechaLocal(fecha);
    if (!date) return '-';
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getEstadoCuota = (cuota) => {
    if (cuota.pagada) {
      return {
        label: 'Pagada',
        icon: <CheckCircleIcon fontSize="small" />,
        color: 'success'
      };
    }
    if (cuota.vencida) {
      return {
        label: 'Vencida',
        icon: <CancelIcon fontSize="small" />,
        color: 'error'
      };
    }
    if (cuota.diasParaVencimiento !== null && cuota.diasParaVencimiento <= 7) {
      return {
        label: 'Por vencer',
        icon: <WarningIcon fontSize="small" />,
        color: 'warning'
      };
    }
    return {
      label: 'Pendiente',
      icon: <AccessTimeIcon fontSize="small" />,
      color: 'info'
    };
  };

  const handlePagar = async (cuota) => {
    try {
      setPagando(true);

      console.log('Iniciando pago de cuota:', cuota);
      
      // Llamar al Context para crear la preferencia
      if (!cuota.identificador) {
        throw new Error('No pudimos identificar la cuota. Refresca e intenta nuevamente.');
      }

      const resultado = await crearPreferenciaPagoCliente(cuota.identificador);

      if (resultado && resultado.init_point) {
        // Abrir Mercado Pago en nueva pestaña
        window.open(resultado.init_point, '_blank');

        // Mostrar mensaje al usuario
        setSnackbar({
          open: true,
          message: 'Se abrió Mercado Pago en una nueva pestaña. Completa el pago y vuelve aquí para ver el resultado.',
          severity: 'info'
        });

        // Iniciar polling para detectar cuando se procese el pago
        iniciarPolling(cuota.cuotaNro);
      }

    } catch (error) {
      console.error('Error al crear preferencia de pago:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Error al iniciar el pago. Por favor, intenta nuevamente.',
        severity: 'error'
      });
    } finally {
      setPagando(false);
    }
  };

  const verificarPagoCuota = async (cuotaNro) => {
    try {
      const response = await getMisCuotasCliente(idItem, idUbicacion);
      const cuotasActualizadas = (response.cuotas || []).sort((a, b) => a.cuotaNro - b.cuotaNro);

      const cuotaPagada = cuotasActualizadas.find(c =>
        c.cuotaNro === cuotaNro && c.pagada
      );

      if (cuotaPagada) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setPolling(false);
        cuotaPagandoRef.current = null;

        setCuotas(cuotasActualizadas);
        setEstadisticas(response.estadisticas || null);

        const primerImpaga = cuotasActualizadas.find(c => !c.pagada);
        if (primerImpaga) {
          setPrimeraCuotaImpagaId(primerImpaga.cuotaNro);
        } else {
          setPrimeraCuotaImpagaId(null);
        }

        setSnackbar({
          open: true,
          message: '¡Pago registrado exitosamente! Tu cuota ha sido actualizada.',
          severity: 'success'
        });

        return true;
      }

      return false;
    } catch (error) {
      console.error('Error verificando pago:', error);
      return false;
    }
  };

  const iniciarPolling = (cuotaNro) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    setPolling(true);
    cuotaPagandoRef.current = cuotaNro;

    let intentos = 0;
    const maxIntentos = 36;  // 36 intentos x 5 segundos ≈ 3 minutos

    pollingIntervalRef.current = setInterval(async () => {
      intentos++;

      try {
        console.log(`Polling intento ${intentos}/${maxIntentos}`);

        const pagoDetectado = await verificarPagoCuota(cuotaNro);
        if (pagoDetectado) {
          return;
        }

        if (intentos >= maxIntentos) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setPolling(false);
          cuotaPagandoRef.current = null;

          setSnackbar({
            open: true,
            message: 'El pago puede tardar unos minutos en procesarse. Recarga la página en unos momentos.',
            severity: 'warning'
          });
        }
      } catch (error) {
        console.error('Error en polling:', error);
      }
    }, 5000);
  };

  const renderBotonPagar = (cuota) => {
    // Si está pagada, mostrar check
    if (cuota.pagada) {
      return (
        <Chip 
          icon={<CheckCircleIcon />} 
          label="Pagada" 
          color="success" 
          size="small" 
        />
      );
    }

    // Si el paquete está cancelado, deshabilitar el botón
    const paqueteCancelado = paqueteInfo?.paqueteCancelado === true;
    
    // Si es la primera cuota impaga, botón habilitado (a menos que esté cancelado)
    if (cuota.cuotaNro === primeraCuotaImpagaId) {
      return (
        <Tooltip title={paqueteCancelado ? "El viaje está cancelado. No se pueden realizar pagos." : ""}>
          <span>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={pagando || polling || paqueteCancelado}
              onClick={() => handlePagar(cuota)}
              startIcon={pagando ? <CircularProgress size={16} color="inherit" /> : <PaymentIcon />}
              sx={{ textTransform: 'none', minWidth: '120px' }}
            >
              {pagando ? 'Procesando...' : 'Pagar Ahora'}
            </Button>
          </span>
        </Tooltip>
      );
    }

    // Si es una cuota futura (hay anteriores impagas), botón deshabilitado
    return (
      <Tooltip title={paqueteCancelado ? "El viaje está cancelado" : "Debes pagar las cuotas anteriores primero"}>
        <span>
          <Button
            variant="outlined"
            size="small"
            disabled
            sx={{ textTransform: 'none', minWidth: '120px' }}
          >
            Pagar
          </Button>
        </span>
      </Tooltip>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Paper sx={{ p: 4, borderRadius: '16px' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Cargando cuotas...</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: '#f5f5f5' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: 'transparent' }}>
        <Toolbar sx={{ backgroundColor: 'transparent' }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleVolver}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <img 
            src={logoTenant || imgLutenteNav} 
            alt="Logo" 
            style={{ height: '40px', marginRight: '16px' }}
          />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'text.primary' }}>
            Detalle de Cuotas
          </Typography>
          {clienteData && (
            <Typography variant="body2" sx={{ mr: 2 }}>
              {clienteData.nombre} {clienteData.apellido}
            </Typography>
          )}
          <IconButton color="inherit" onClick={handleLogout} title="Cerrar sesión">
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Contenido */}
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Alerta de viaje cancelado */}
        {paqueteInfo?.paqueteCancelado === true && (
          <Alert 
            severity="error" 
            icon={<WarningIcon fontSize="large" />}
            sx={{ 
              mb: 3, 
              borderRadius: '16px',
              fontSize: '1.1rem',
              fontWeight: 600,
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              ⚠️ VIAJE CANCELADO
            </Typography>
            <Typography variant="body2">
              Este viaje ha sido cancelado. No se pueden realizar nuevos pagos de cuotas en este momento.
              Para más información, contactá con la agencia.
            </Typography>
          </Alert>
        )}

        {/* Info del Paquete */}
        {paqueteInfo && (
          <Paper sx={{ p: 3, mb: 3, borderRadius: '16px' }}>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              {paqueteInfo.item?.descripcion}
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              📍 {paqueteInfo.ubicacion?.descripcion}
            </Typography>
            {/* <Typography variant="caption" color="text.secondary">
              Código: {paqueteInfo.item?.codigo}
            </Typography> */}
          </Paper>
        )}

        {/* Errores */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
            {error}
          </Alert>
        )}

        {/* Resumen Estadísticas */}
        {estadisticas && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={true}>
              <Card sx={{ borderRadius: '16px', height: '100%' }}>
                <CardContent>              
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{paddingBottom: '6px'}}>
                    Precio Total del Viaje
                  </Typography>
                  {/* <Typography variant="caption" color="text.secondary">
                    (Incluye intereses de cuotas vencidas)
                  </Typography> */}         
                  <Typography variant="h5" fontWeight="bold">
                    {formatearMoneda(estadisticas.montoTotal)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={true}>
              <Card sx={{ borderRadius: '16px', height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Cuotas
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {estadisticas.totalCuotas}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={true}>
              <Card sx={{ borderRadius: '16px', bgcolor: 'success.lighter', height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Cuotas Pagadas
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {estadisticas.cuotasPagadas}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={true}>
              <Card sx={{ borderRadius: '16px', bgcolor: 'warning.lighter', height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Cuotas Pendientes
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {estadisticas.cuotasPendientes}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={true}>
              <Card sx={{ borderRadius: '16px', bgcolor: 'error.lighter', height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{paddingBottom: '6px'}}>
                    Monto Pendiente
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="error.main">
                    {formatearMoneda(estadisticas.montoPendiente)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Botón Descargar Cuponera */}
        {usaCuponera && cuotas.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={generandoCuponera}
              onClick={handleDescargarCuponera}
              startIcon={generandoCuponera ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 'bold'
              }}
            >
              {generandoCuponera ? 'Generando cuponera...' : 'Descargar Cuponera'}
            </Button>
          </Box>
        )}

        {/* Tabla de Cuotas */}
        {cuotas.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '16px' }}>
            <Typography variant="h6" color="text.secondary">
              No se encontraron cuotas para este paquete
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: '16px' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#5b5b5f' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Cuota Nro
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    1er Vto
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    2do Vto
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                    Descuento
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                    Interés
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                    Importe a pagar
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Estado
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Fecha de Pago
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cuotas.map((cuota) => {
                  const estado = getEstadoCuota(cuota);
                  return (
                    <TableRow 
                      key={cuota.cuotaNro}
                      sx={{
                        '&:nth-of-type(odd)': { bgcolor: 'grey.50' },
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold">
                          Cuota {cuota.cuotaNro}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatearFecha(cuota.fechaPrimerVto)}</TableCell>
                      <TableCell>{formatearFecha(cuota.fechaSegundoVto)}</TableCell>
                      <TableCell align="right">
                        {!cuota.montoDescuento || Number(cuota.montoDescuento) === 0
                          ? <Typography variant="body2" color="text.secondary">-</Typography>
                          : <Typography variant="body2" color="error.main">{formatearMoneda(cuota.montoDescuento)}</Typography>}
                      </TableCell>
                      <TableCell align="right">
                        {cuota.pagada || !cuota.montoInteres || cuota.montoInteres === 0 || (cuota.fechaSegundoVto && parseFechaLocal(cuota.fechaSegundoVto) > new Date())
                          ? <Typography variant="body2" color="text.secondary">-</Typography>
                          : <Typography variant="body2" color="error.main">{formatearMoneda(cuota.montoInteres)}</Typography>}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="bold">
                          {formatearMoneda(cuota.importeAPagar ?? (cuota.montoBase || 0))}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Chip 
                          icon={estado.icon}
                          label={estado.label}
                          color={estado.color}
                          size="small"
                        />
                      </TableCell>
                      
                      <TableCell>
                        {cuota.pagada ? (
                          <Typography variant="body2" color="success.main">
                            ✓ {formatearFecha(cuota.fechaPago)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        {renderBotonPagar(cuota)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Snackbar para mensajes */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Totales */}
        {estadisticas && (
          <Paper sx={{ p: 3, mt: 3, borderRadius: '16px' }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Resumen de Pagos
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body1" color="text.secondary">
                      Total Actualizado:
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      (con intereses vencidos)
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="bold" color="primary.main">
                    {formatearMoneda(estadisticas.montoTotal)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" color="success.main">
                    Monto Pagado:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="success.main">
                    {formatearMoneda(estadisticas.montoPagado)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" color="error.main">
                    Monto Pendiente:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="error.main">
                    {formatearMoneda(estadisticas.montoPendiente)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Botón Volver */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleVolver}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              px: 4,
              py: 1.5,
            }}
          >
            Volver a Mis Paquetes
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ClienteCuotasDetalle;




